import type { FlexiObservation } from '@/features/text-mode/api'
import type { ResponseOption, TextModePatientInfo } from '@/features/text-mode/types/text-mode.types'
import type {
  GuidedAgeUnit,
  GuidedAnswer,
  GuidedAssessmentPath,
  GuidedMaternalStatus,
  GuidedModeCollection,
  GuidedModeDefinition,
  GuidedModeObservation,
  GuidedModeQuestionDefinition,
  GuidedModeStep,
  GuidedPatientSex,
} from '../types/guided-mode.types'
import { convertAgeToDays } from './assessment-selection'

export const DEMOGRAPHIC_CONCEPT_IDS = new Set([
  'concept.patient_id',
  'concept.age',
  'concept.gender',
  'concept.maternal_status',
])

export interface GuidedPatientDetails {
  age: number | undefined
  ageUnit: GuidedAgeUnit
  sex: GuidedPatientSex
  maternalStatus: GuidedMaternalStatus
  assessmentPath: GuidedAssessmentPath | null
  patientId?: GuidedAnswer
}

export function flattenSteps(definition: GuidedModeDefinition | null | undefined): GuidedModeStep[] {
  return (definition?.assessment?.steps || [])
    .flatMap(step => (step.kind === 'group' ? step.steps || [] : [step]))
}

function isAnsweredValue(answer: GuidedAnswer): boolean {
  if (Array.isArray(answer)) return answer.length > 0
  return answer !== undefined && answer !== null && answer !== ''
}

function responseOptions(question: GuidedModeQuestionDefinition): ResponseOption[] | null {
  const tokens = question.responseTokens
  if (!tokens) return null

  const options = Object.entries(tokens).map(([value, token]) => ({ value, token }))
  return options.length > 0 ? options : null
}

function numericOptions(collection?: GuidedModeCollection) {
  if (collection?.type !== 'numeric') return null

  return {
    min: collection.min ?? 0,
    max: collection.max ?? 0,
    unit: collection.displayUnit ?? '',
    decimalPlaces: collection.decimalPlaces ?? 0,
    allowUnknown: Boolean(collection.allowUnknown),
  }
}

interface QuestionMetadata {
  nameToken?: string
  responseOptions: ResponseOption[] | null
  numericOptions: ReturnType<typeof numericOptions>
}

function groupItemMetadata(code: string): QuestionMetadata {
  return {
    nameToken: `responses.${code}.title`,
    responseOptions: null,
    numericOptions: null,
  }
}

function questionMetadata(
  question: GuidedModeQuestionDefinition,
  step?: GuidedModeStep,
): QuestionMetadata {
  return {
    nameToken: question.titleToken,
    responseOptions: responseOptions(question),
    numericOptions: numericOptions(step?.collection),
  }
}

function indexMetadataByCode(
  definition: GuidedModeDefinition,
  steps: GuidedModeStep[],
): Map<string, QuestionMetadata> {
  const stepByQuestionId = new Map(
    steps.filter(step => step.questionId).map(step => [step.questionId as string, step]),
  )
  const codeOf = (conceptId: string): string | undefined =>
    definition.concepts?.[conceptId]?.code?.code

  const metadata = new Map<string, QuestionMetadata>()

  Object.values(definition.questions || {}).forEach((question) => {
    if (question.outputs?.length) {
      question.outputs.forEach((output) => {
        const code = codeOf(output.conceptId)
        if (code) metadata.set(code, groupItemMetadata(code))
      })
      return
    }

    const code = codeOf(question.conceptId)
    if (code) metadata.set(code, questionMetadata(question, stepByQuestionId.get(question.id)))
  })

  return metadata
}

interface BuildReviewObservationsArgs {
  definition: GuidedModeDefinition | null | undefined
  steps: GuidedModeStep[]
  observations: GuidedModeObservation[]
}

export function buildReviewObservations({
  definition,
  steps,
  observations,
}: BuildReviewObservationsArgs): FlexiObservation[] {
  if (!definition) return []

  const metadataByCode = indexMetadataByCode(definition, steps)

  return observations.map((observation) => {
    const metadata = metadataByCode.get(observation.name)

    return {
      ...observation,
      ...(metadata?.nameToken ? { nameToken: metadata.nameToken } : {}),
      responseOptions: metadata?.responseOptions ?? null,
      numericOptions: metadata?.numericOptions ?? null,
    } as FlexiObservation
  })
}

export function buildGuidedPatient(details: GuidedPatientDetails): TextModePatientInfo {
  const ageInDays = convertAgeToDays(details.age, details.ageUnit)
  const patientId = isAnsweredValue(details.patientId) ? String(details.patientId).trim() : ''

  return {
    id: patientId || null,
    ageInDays,
    sex: details.sex || null,
    maternal_status: details.maternalStatus || 'none',
    type: details.assessmentPath,
  }
}
